/* eslint-disable no-console */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useSpace } from '@ably/spaces/react';
import type { Lock, SpaceMember } from '@ably/spaces';
import {
  QUOTE_PRICING_FIELDS,
  quoteItemFieldLockId,
  quoteSectionLockId,
  type QuoteItemField
} from 'lib/realtime/locks';
import type { RfqMemberProfileData } from 'lib/realtime/RfqSpacesProvider';

// Grace period before a lock is released on blur, so moving the cursor between
// a field's sub-parts (e.g. the quantity +/- buttons) doesn't flicker the lock.
const RELEASE_GRACE_MS = 500;

interface FieldLockContextValue {
  /** Locks currently held by OTHER members, keyed by lock id. */
  locksByField: Map<string, SpaceMember>;
  acquire: (itemId: string, field: QuoteItemField) => void;
  release: (itemId: string, field: QuoteItemField) => void;
  /** Raw acquire/release by lock id (e.g. section locks). */
  acquireLock: (lockId: string, attributes?: Record<string, unknown>) => void;
  releaseLock: (lockId: string) => void;
}

const noop = () => {};

const FieldLockContext = createContext<FieldLockContextValue>({
  locksByField: new Map(),
  acquire: noop,
  release: noop,
  acquireLock: noop,
  releaseLock: noop
});

/**
 * Holds a single Spaces locks subscription for the whole quote product table and
 * exposes acquire/release for per-field edit locks. Mounted inside the quote's
 * `CollabSpacesProvider`; no-ops (empty map, noop acquire/release) while the
 * Space isn't ready, mirroring how `useQuoteRowFocus` tolerates that window.
 */
export const QuoteFieldLockProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { space } = useSpace();
  const [locksByField, setLocksByField] = useState<Map<string, SpaceMember>>(
    new Map()
  );

  useEffect(() => {
    if (!space) {
      setLocksByField(new Map());
      return undefined;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        // getOthers() already excludes our own locks.
        const others = await space.locks.getOthers();
        if (cancelled) return;
        const map = new Map<string, SpaceMember>();
        others.forEach(lock => {
          if (lock.status === 'locked' && lock.member) {
            map.set(lock.id, lock.member);
          }
        });
        setLocksByField(map);
      } catch (err) {
        console.warn('[quote-locks] failed to read locks', err);
      }
    };

    refresh();
    const handler = (_lock: Lock) => refresh();
    space.locks.subscribe('update', handler);

    return () => {
      cancelled = true;
      try {
        space.locks.unsubscribe('update', handler);
      } catch (err) {
        console.warn('[quote-locks] unsubscribe failed', err);
      }
    };
  }, [space]);

  const acquireLock = useCallback(
    (lockId: string, attributes?: Record<string, unknown>) => {
      if (!space) return;
      space.locks
        .acquire(lockId, attributes ? { attributes } : undefined)
        .catch(err =>
          // Rejected when another member already holds it — expected; the
          // subscription will reflect the real holder.
          console.info('[quote-locks] acquire rejected', { lockId, err })
        );
    },
    [space]
  );

  const releaseLock = useCallback(
    (lockId: string) => {
      if (!space) return;
      space.locks.release(lockId).catch(() => {
        // Expected if we never held it (acquire was rejected).
      });
    },
    [space]
  );

  const acquire = useCallback(
    (itemId: string, field: QuoteItemField) =>
      acquireLock(quoteItemFieldLockId(itemId, field), { field }),
    [acquireLock]
  );

  const release = useCallback(
    (itemId: string, field: QuoteItemField) =>
      releaseLock(quoteItemFieldLockId(itemId, field)),
    [releaseLock]
  );

  const value = useMemo(
    () => ({ locksByField, acquire, release, acquireLock, releaseLock }),
    [locksByField, acquire, release, acquireLock, releaseLock]
  );

  return (
    <FieldLockContext.Provider value={value}>
      {children}
    </FieldLockContext.Provider>
  );
};

export interface FieldLockState {
  /** True when another member currently holds this field's lock. */
  lockedByOther: boolean;
  /** Profile of the member editing this field, when locked by someone else. */
  holder: RfqMemberProfileData | null;
  /** Claim the lock (call on focus). Cancels any pending release. */
  acquire: () => void;
  /** Release the lock after a short grace period (call on blur). */
  release: () => void;
}

/**
 * Inert lock state for surfaces rendered outside a QuoteFieldLockProvider (e.g.
 * the Auto RFQ quote tab, which reuses the price/GP columns without collab).
 */
export const NO_FIELD_LOCK: FieldLockState = {
  lockedByOther: false,
  holder: null,
  acquire: () => {},
  release: () => {}
};

/**
 * Per-input lock state for one quote item field. Reads the shared lock map for
 * the "who's editing" indicator and wires focus/blur acquire/release with a
 * grace period. Releases on unmount if still held (covers virtualized rows
 * scrolling out while focused).
 */
export const useFieldLock = (
  itemId: string,
  field: QuoteItemField
): FieldLockState => {
  const { locksByField, acquire, release } = useContext(FieldLockContext);
  const lockId = quoteItemFieldLockId(itemId, field);

  const member = locksByField.get(lockId);
  const lockedByOther = !!member;
  const holder =
    (member?.profileData as unknown as RfqMemberProfileData) ?? null;

  const releaseTimer = useRef<ReturnType<typeof setTimeout>>();
  const acquiredRef = useRef(false);
  // Keep latest acquire/release without re-creating the focus callbacks.
  const acquireRef = useRef(acquire);
  const releaseRef = useRef(release);
  acquireRef.current = acquire;
  releaseRef.current = release;

  const doAcquire = useCallback(() => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = undefined;
    }
    acquiredRef.current = true;
    acquireRef.current(itemId, field);
  }, [itemId, field]);

  const doRelease = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      acquiredRef.current = false;
      releaseRef.current(itemId, field);
      releaseTimer.current = undefined;
    }, RELEASE_GRACE_MS);
  }, [itemId, field]);

  useEffect(
    () => () => {
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
      if (acquiredRef.current) releaseRef.current(itemId, field);
    },
    [itemId, field]
  );

  return { lockedByOther, holder, acquire: doAcquire, release: doRelease };
};

export interface PricingLockState {
  /** True when another member holds ANY of the pricing locks on this item. */
  lockedByOther: boolean;
  /** Profile of whoever holds a pricing lock (first match), when locked. */
  holder: RfqMemberProfileData | null;
  /** Per-input lock state — the avatar shows only on the input actually held. */
  sell: FieldLockState;
  gp: FieldLockState;
  gpPercent: FieldLockState;
}

/**
 * Pricing lock for a quote item. Unit Price (`sell`), GP$ (`gp`) and GP%
 * (`gpPercent`) each lock independently so the editor's avatar appears only on
 * the input they're actually editing — but the row disables all three together
 * (`lockedByOther`) since they recalculate each other. One instance per row
 * (each lock id has a single grace timer, so moving between the inputs doesn't
 * race a shared lock).
 */
export const usePricingLock = (itemId: string): PricingLockState => {
  const sell = useFieldLock(itemId, 'sell');
  const gp = useFieldLock(itemId, 'gp');
  const gpPercent = useFieldLock(itemId, 'gpPercent');

  return {
    lockedByOther:
      sell.lockedByOther || gp.lockedByOther || gpPercent.lockedByOther,
    holder: sell.holder || gp.holder || gpPercent.holder,
    sell,
    gp,
    gpPercent
  };
};

/**
 * Raw acquire/release for callers that lock a quote item field whose id is only
 * known at call time (e.g. a dialog opened for a dynamically chosen option row),
 * so they can't use the per-input `useFieldLock`. Acquire/release immediately —
 * pair them with the dialog's open/close so the lock lives exactly as long as
 * the dialog is mounted.
 */
export const useFieldLockActions = (): {
  acquire: (itemId: string, field: QuoteItemField) => void;
  release: (itemId: string, field: QuoteItemField) => void;
} => {
  const { acquire, release } = useContext(FieldLockContext);
  return { acquire, release };
};

/**
 * Item-level lock state across ANY editable field (pricing, note, label,
 * options, remove). Used to block destructive row actions (Remove Item /
 * Remove Option) while another collaborator is working on the item in any way.
 * Other actions stay enabled — only their own field/dialog locks disable them.
 */
export const useItemAnyFieldLock = (
  itemId: string
): { lockedByOther: boolean; holder: RfqMemberProfileData | null } => {
  const { locksByField } = useContext(FieldLockContext);

  const member = useMemo(() => {
    const prefix = `quoteItem:${itemId}:`;
    const found = Array.from(locksByField.entries()).find(([id]) =>
      id.startsWith(prefix)
    );
    return found?.[1];
  }, [locksByField, itemId]);

  return {
    lockedByOther: !!member,
    holder: (member?.profileData as unknown as RfqMemberProfileData) ?? null
  };
};

/**
 * "Is any collaborator editing pricing anywhere on this quote?" — for the
 * header Refresh All button, which lives outside the QuoteFieldLockProvider but
 * inside the Space, so it keeps its own lightweight locks subscription. Returns
 * true while another member holds any per-item `pricing` lock.
 */
export const useAnyPricingLock = (): {
  lockedByOther: boolean;
  holder: RfqMemberProfileData | null;
} => {
  const { space } = useSpace();
  const [state, setState] = useState<{
    lockedByOther: boolean;
    holder: RfqMemberProfileData | null;
  }>({ lockedByOther: false, holder: null });

  useEffect(() => {
    if (!space) {
      setState({ lockedByOther: false, holder: null });
      return undefined;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const others = await space.locks.getOthers();
        if (cancelled) return;
        const pricing = others.find(
          lock =>
            lock.status === 'locked' &&
            QUOTE_PRICING_FIELDS.some(field => lock.id.endsWith(`:${field}`))
        );
        setState({
          lockedByOther: !!pricing,
          holder:
            (pricing?.member?.profileData as unknown as RfqMemberProfileData) ??
            null
        });
      } catch (err) {
        console.warn('[quote-locks] failed to read pricing locks', err);
      }
    };

    refresh();
    const handler = (_lock: Lock) => refresh();
    space.locks.subscribe('update', handler);
    return () => {
      cancelled = true;
      try {
        space.locks.unsubscribe('update', handler);
      } catch (err) {
        console.warn('[quote-locks] unsubscribe failed', err);
      }
    };
  }, [space]);

  return state;
};

export interface SectionLockState {
  /** True when another member currently holds this section's lock. */
  lockedByOther: boolean;
  /** Profile of the member acting on this section, when locked by someone else. */
  holder: RfqMemberProfileData | null;
  /** Claim the section lock (call when opening a section action/dialog). */
  acquire: () => void;
  /** Release the section lock after a short grace period (call on close). */
  release: () => void;
}

/**
 * Section-level lock state. A single lock per section is held while THIS user
 * has any section action open (edit, add items, move, remove), so other
 * collaborators see the whole section action menu disabled. Mirrors
 * `useFieldLock`'s grace-period release and unmount cleanup.
 */
export const useSectionLock = (sectionId: string): SectionLockState => {
  const { locksByField, acquireLock, releaseLock } =
    useContext(FieldLockContext);
  const lockId = quoteSectionLockId(sectionId);

  const member = locksByField.get(lockId);
  const lockedByOther = !!member;
  const holder =
    (member?.profileData as unknown as RfqMemberProfileData) ?? null;

  const releaseTimer = useRef<ReturnType<typeof setTimeout>>();
  const acquiredRef = useRef(false);
  const acquireRef = useRef(acquireLock);
  const releaseRef = useRef(releaseLock);
  acquireRef.current = acquireLock;
  releaseRef.current = releaseLock;

  const doAcquire = useCallback(() => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = undefined;
    }
    acquiredRef.current = true;
    acquireRef.current(lockId);
  }, [lockId]);

  const doRelease = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      acquiredRef.current = false;
      releaseRef.current(lockId);
      releaseTimer.current = undefined;
    }, RELEASE_GRACE_MS);
  }, [lockId]);

  useEffect(
    () => () => {
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
      if (acquiredRef.current) releaseRef.current(lockId);
    },
    [lockId]
  );

  return { lockedByOther, holder, acquire: doAcquire, release: doRelease };
};
